<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import MFilterButton from "../MFilterButton";
import MHighlightSearch from "../MHighlightSearch";
import MLayoutStack from "../MLayoutStack";
import { DateHeading, JournalEntryType, PatientJournalGroup, PatientJournalRecord } from "./types";
import { computed } from "@vue/reactivity";
import { useDebouncedThrottledFunction } from "../../../composables/throttleDebounce";
import { itemPositionAboveViewport } from "../../../utils/scroll";

import ConsultationJournalPresenter from "./presenters/ConsultationJournalPresenter.vue";
import AllergyHistoryJournalPresenter from "./presenters/AllergyHistoryJournalPresenter.vue";
import NoteJournalPresenter from "./presenters/NoteJournalPresenter.vue";
import ObservationJournalPresenter from "./presenters/ObservationJournalPresenter.vue";
import FitNoteJournalPresenter from "./presenters/FitNoteJournalPresenter.vue";
import CommunicationJournalPesenter from "./presenters/CommunicationJournalPesenter.vue";
import PrescriptionJournalPresenter from "./presenters/PrescriptionJournalPresenter.vue";
import ImmunisationJournalPresenter from "./presenters/ImmunisationJournalPresenter.vue";
import DocumentJournalPresenter from "./presenters/DocumentJournalPresenter.vue";
import FutureActionJournalPresenter from "./presenters/FutureActionJournalPresenter.vue";
import InvestigationJournalPresenter from "./presenters/InvestigationJournalPresenter.vue";
import InvestigationRequestJournalPresenter from "./presenters/InvestigationRequestJournalPresenter.vue";
import ReferralJournalPresenter from "./presenters/ReferralJournalPresenter.vue";
import MedicationAdministrationJournalPresenter from "./presenters/MedicationAdministrationJournalPresenter.vue";
import MedicationStatementOverTheCounterJournalPresenter from "./presenters/MedicationStatementOverTheCounterJournalPresenter.vue";
import ProcedureJournalPresenter from "./presenters/ProcedureJournalPresenter.vue";
import MedicationStatementPrescribedElsewhereJournalPresenter from "./presenters/MedicationStatementPrescribedElsewhereJournalPresenter.vue";
import { generateUUIDv7 } from "../../../utils/helpers";
import { getScrollParent } from "../../../utils/misc";

const props = defineProps({
  items: {
    type: Array as () => PatientJournalGroup[],
    default: () => [],
  },
  filterCount: Number,
  showFilter: {
    type: Boolean,
  },
  readOnly: {
    type: Boolean,
  },
  showSearch: {
    type: Boolean,
  },
});

const allItems = computed(() =>
  props.items
    .flatMap(({ title, items }) => [
      { title, isDateHeading: true, id: generateUUIDv7() } as DateHeading,
      ...items,
    ])
    .map((item, index) => {
      return {
        ...item,
        allItemsIndex: index,
      };
    }),
);
const visibleItems = ref<
  ((PatientJournalRecord & { mounted?: boolean; allItemsIndex: number }) | DateHeading)[]
>([]);
const itemsPerLoad = 40;
const currentIndex = ref(0);
const allItemsLoaded = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const journalTimelineRef = ref<HTMLDivElement>();
const scrollPanelEl = ref<HTMLElement>();

function loadNextPageOfItems() {
  if (allItemsLoaded.value === false) {
    isLoading.value = true;
    const nextBatch = allItems.value.slice(currentIndex.value, currentIndex.value + itemsPerLoad);
    currentIndex.value = currentIndex.value + itemsPerLoad;
    visibleItems.value.push(...nextBatch);

    if (nextBatch.length < itemsPerLoad || visibleItems.value.length === allItems.value.length) {
      allItemsLoaded.value = true;
    }

    isLoading.value = false;
  }
}

const scrollParent = ref<HTMLElement>();
const journalDates = ref<HTMLElement[]>();
const sectionHeader = ref<string>();

const setScrollWatchers = (e) => {
  debouncedThrottledHandleScroll(e);
  debouncedThrottledHandleScrollDates(e);
};

const removeScrollWatchers = () => {
  scrollPanelEl.value.removeEventListener("scroll", debouncedThrottledHandleScroll);
  scrollPanelEl.value.removeEventListener("scroll", debouncedThrottledHandleScrollDates);
};

function handleScroll(e: Event) {
  if (e.target && "scrollTop" in e.target) {
    const el = e.target as HTMLDivElement;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const remainingHeight = scrollHeight - scrollTop - el.clientHeight;

    if (remainingHeight < 800 && isLoading.value === false) {
      loadNextPageOfItems();
    }
  }
}

const setFixedDates = () => {
  const items = journalDates?.value ?? [];
  const itemsAboveView = items.filter((e) => itemPositionAboveViewport(e, scrollParent.value, 45));
  if (itemsAboveView.length) {
    sectionHeader.value = itemsAboveView?.[itemsAboveView.length - 1]?.textContent;
  }
};

const setFirstFixedDate = () => {
  if (journalDates?.value) {
    sectionHeader.value = journalDates.value[0].textContent;
    journalDates.value[0].style.display = "none";
  }
};

const { debouncedThrottled: debouncedThrottledHandleScroll } = useDebouncedThrottledFunction(
  handleScroll,
  200,
  500,
);

const { debouncedThrottled: debouncedThrottledHandleScrollDates } = useDebouncedThrottledFunction(
  setFixedDates,
  50,
  100,
);

onMounted(async () => {
  scrollParent.value = getScrollParent(journalTimelineRef.value!);
  // console.log("onMounted", props.items);
  await loadNextPageOfItems();

  if (scrollParent.value) {
    scrollPanelEl.value = scrollParent.value as HTMLElement;
    scrollPanelEl.value.addEventListener("scroll", setScrollWatchers);
  }
  setFirstFixedDate();
});

onBeforeUnmount(() => {
  if (scrollPanelEl.value) {
    scrollPanelEl.value.removeEventListener("scroll", removeScrollWatchers);
  }
});

const careRecordTypeToComponentMap: Record<JournalEntryType, any> = {
  [JournalEntryType.ENCOUNTER]: ConsultationJournalPresenter,
  [JournalEntryType.ALLERGY_HISTORY]: AllergyHistoryJournalPresenter,
  [JournalEntryType.NOTE]: NoteJournalPresenter,
  [JournalEntryType.OBSERVATION]: ObservationJournalPresenter,
  [JournalEntryType.FIT_NOTE]: FitNoteJournalPresenter,
  [JournalEntryType.COMMUNICATION]: CommunicationJournalPesenter,
  [JournalEntryType.PRESCRIPTION]: PrescriptionJournalPresenter,
  [JournalEntryType.IMMUNISATION]: ImmunisationJournalPresenter,
  [JournalEntryType.MEDICATION_ADMINISTRATION]: MedicationAdministrationJournalPresenter,
  [JournalEntryType.MEDICATION_STATEMENT_PRESCRIBED_ELSEWHERE]:
    MedicationStatementPrescribedElsewhereJournalPresenter,
  [JournalEntryType.MEDICATION_STATEMENT_OVER_THE_COUNTER]:
    MedicationStatementOverTheCounterJournalPresenter,
  [JournalEntryType.FUTURE_ACTION]: FutureActionJournalPresenter,
  [JournalEntryType.REFERRAL]: ReferralJournalPresenter,
  [JournalEntryType.DOCUMENT]: DocumentJournalPresenter,
  [JournalEntryType.PROCEDURE]: ProcedureJournalPresenter,
  [JournalEntryType.INVESTIGATION]: InvestigationJournalPresenter,
  [JournalEntryType.INVESTIGATION_REQUEST]: InvestigationRequestJournalPresenter,

  [JournalEntryType.COMMUNICATION_THREAD]: undefined,
  [JournalEntryType.PROBLEM]: undefined,
};
</script>
<template>
  <div ref="journalTimelineRef" class="journal-timeline">
    <div v-if="sectionHeader" class="section-header-fixed">
      <span class="journal-group-title">{{ sectionHeader }}</span>
    </div>
    <div v-if="showSearch" class="journal-filter q-pa-xs q-pb-md">
      <m-highlight-search placeholder="Text search" :content="$el" show-icon />
      <m-filter-button
        v-if="showFilter"
        show-icon
        label="Filters"
        color="grey"
        :filter-count="filterCount"
        borderless
      >
        <slot name="filter" />
      </m-filter-button>
    </div>
    <div v-if="showFilter" ref="filter-placeholder" class="filter-placeholder" />

    <Transition name="fade">
      <m-layout-stack v-if="visibleItems.length > 0" gap="2" class="word-break">
        <template v-for="item in visibleItems" :key="item.id">
          <component
            :is="careRecordTypeToComponentMap[item.type] ?? 'div'"
            v-if="'type' in item"
            ref="journalCards"
            :entry="item"
            :read-only="readOnly"
            :data-index="`${item.allItemsIndex}`"
          />
          <div
            v-else-if="'isDateHeading' in item"
            ref="journalDates"
            class="journal-group-title"
            :data-index="`${item.allItemsIndex}`"
          >
            {{ item.title }}
          </div>
        </template>
      </m-layout-stack>
    </Transition>
  </div>
</template>
<style lang="scss">
.section-header-fixed {
  position: sticky;
  z-index: 901;
  top: 0;
  padding-bottom: var(--gap-1);
  width: 100%;
  background: var(--medicus-background);
}
.journal-timeline {
  display: flex;
  flex-direction: column;
  position: relative;
  // to stop focus outline being cropped
  padding-left: 4px;

  padding-top: 0 !important;

  .journal-group-title {
    color: var(--grey-darkest);
    font-weight: bold;
  }

  .m-card + .journal-group-title {
    margin-top: var(--gap-1);
  }

  .word-break {
    word-break: break-word;
  }

  .journal-item {
    //padding-left: 27px;

    position: relative;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;

    .journal-item--text {
      font-family: Arial, sans-serif;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 20px;
      color: var(--grey-darkest);
      .text-with-separator:not(:first-child)::before {
        content: " • ";
      }
    }
  }

  .journal-filter {
    z-index: 10;
    position: sticky;
    top: 0px;

    background: var(--medicus-background);
    .medicus-button-container[color="grey"] {
      .medicus-button {
        font-weight: 400;
        background: var(--medicus-background);
      }
    }
    /* Large Drop Shadow */
    .shadow {
      box-shadow:
        0px 4px 6px rgba(0, 0, 0, 0.06),
        0px 2px 4px rgba(0, 0, 0, 0.06);
    }

    .q-field__prepend {
      padding-right: 0;
    }
  }
}
</style>
